// App.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { OutcomeSelection } from './components/OutcomeSelection';
import { ConfirmPathScreen } from './components/ConfirmPathScreen';
import { BenchmarkOverview } from './components/BenchmarkOverview';
import { SpeakingScreen } from './components/SpeakingScreen';
import { ListeningAssessment } from './components/ListeningAssessment';
import { OnboardingWriting } from './components/OnboardingWriting';
import { OnboardingScreen7 } from './components/OnboardingScreen7';
import { DiagnosticResultsScreen, type DiagnosticData } from './components/DiagnosticResultsScreen';
import { WeekPlanScreen } from './components/WeekPlanScreen';
import { OnboardingSuccess } from './components/OnboardingSuccess';
import { DemoEndScreen } from './components/DemoEndScreen';
type Outcome = 'exam' | 'presentation' | 'interview' | 'authority' | 'everyday' | 'custom' | null;
type PathType = 'academic' | 'business' | 'social';
type AssessmentType = 'speaking' | 'listening' | 'writing' | 'reading';
type Language = 'de' | 'en';

// Disce Farben
const disceColors = {
  primary: {
    700: '#AD5A00',
    900: '#8B4513',
    500: '#4CAF50',
    300: '#6FBF73'
  },
  neutral: {
    0: '#FFFFFF',
    600: '#666666'
  }
};
const disceTypography = {
  fontFamily: '"Cormorant Garamond", Georgia, "Times New Roman", serif',
  button: {
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: 1.4
  }
};

// Cover-Bilder: Desktop vs. Mobile
const COVER_URL_DESKTOP = 'https://disce-app.github.io/Minimal_Detailed_Image.jpeg';
const COVER_URL_MOBILE = 'https://disce-app.github.io/Cover2.png'; // Neues Mobile-Cover

// Overlay-Komponente für das Deckblatt mit responsivem Bild
function CoverOverlay({
  show,
  onDismiss,
  isMobile
}: {
  show: boolean;
  onDismiss: () => void;
  isMobile: boolean;
}) {
  return <div onClick={onDismiss} style={{
    position: 'absolute',
    inset: 0,
    zIndex: 100,
    backgroundImage: `url(${isMobile ? COVER_URL_MOBILE : COVER_URL_DESKTOP})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    cursor: 'pointer',
    transform: show ? 'translateX(0)' : 'translateX(-100%)',
    transition: 'transform 0.45s ease-in-out',
    pointerEvents: show ? 'auto' : 'none'
  }} data-id="element-0" />;
}
export function App() {
  const [currentStep, setCurrentStep] = useState(0);
  const [language, setLanguage] = useState<Language>('de');
  const [outcome, setOutcome] = useState<Outcome>(null);
  const [customText, setCustomText] = useState<string>();
  const [selectedPath, setSelectedPath] = useState<PathType>('business');
  const [completedAssessments, setCompletedAssessments] = useState<Set<AssessmentType>>(new Set());
  const [showCover, setShowCover] = useState(true);
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    const update = () => {
      setIsNarrow(window.innerWidth <= 768);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  const toggleLanguage = () => {
    setLanguage(prev => prev === 'de' ? 'en' : 'de');
  };
  const assessmentStatus = useMemo(() => {
    return {
      speaking: completedAssessments.has('speaking'),
      listening: completedAssessments.has('listening'),
      writing: completedAssessments.has('writing'),
      reading: completedAssessments.has('reading')
    };
  }, [completedAssessments]);
  const completedCount = useMemo(() => Number(assessmentStatus.speaking) + Number(assessmentStatus.listening) + Number(assessmentStatus.writing) + Number(assessmentStatus.reading), [assessmentStatus]);
  const checkProgress = completedCount / 4 * 100;
  const diagnosticData: DiagnosticData = {
    cefr: {
      speaking: 'B2',
      listening: 'C1',
      writing: 'B2',
      reading: 'C1'
    },
    speaking: {
      speechRate: 120,
      fillerRate: 1.2,
      prosodyScore: 'good',
      levelMatch: 85
    },
    writing: {
      registerAccuracy: 'good',
      cohesionScore: 7,
      lexicalPrecision: 85,
      errorDensity: 0.5
    },
    listeningComprehension: 82,
    readingComprehension: 88,
    skillBalance: {
      balanced: true,
      message: 'Skills are well-balanced across all areas'
    },
    goalAlignment: {
      outcome: outcome === 'custom' ? customText || 'Custom goal' : outcome || 'General fluency',
      gapPoints: 2
    },
    recommendedIntensity: 'full'
  };
  const resetOnboarding = () => {
    setOutcome(null);
    setCustomText(undefined);
    setSelectedPath('business');
    setCompletedAssessments(new Set());
  };
  const handleGoBack = () => {
    if (currentStep === 1) {
      resetOnboarding();
      setCurrentStep(0);
      return;
    }
    if (currentStep > 0) {
      if (currentStep >= 3 && currentStep <= 6) {
        setCurrentStep(2);
      } else {
        setCurrentStep(currentStep - 1);
      }
    }
  };
  const handleOutcomeSelection = (selectedOutcome: Outcome, text?: string) => {
    setOutcome(selectedOutcome);
    setCustomText(text);
    setCurrentStep(1);
  };
  const handlePathConfirm = (path: PathType) => {
    setSelectedPath(path);
    setCurrentStep(2);
  };
  const handleStartAssessment = (type: AssessmentType) => {
    switch (type) {
      case 'speaking':
        setCurrentStep(3);
        break;
      case 'listening':
        setCurrentStep(4);
        break;
      case 'writing':
        setCurrentStep(5);
        break;
      case 'reading':
        setCurrentStep(6);
        break;
    }
  };
  const handleAssessmentComplete = (type: AssessmentType) => {
    setCompletedAssessments(prev => {
      const next = new Set(prev);
      next.add(type);
      if (next.size === 4) {
        setCurrentStep(7);
      } else {
        setCurrentStep(2);
      }
      return next;
    });
  };
  const handleAcceptPlan = () => {
    setCurrentStep(8);
  };
  const handleWeekPlanAccept = () => {
    setCurrentStep(9);
  };
  const handleStartFirstSession = () => {
    setCurrentStep(10);
  };
  const handleGoToHomeHub = () => {
    console.log('Going to Home Hub');
  };
  const handleSubmitEmail = (email: string) => {
    console.log('User submitted email:', email);
  };
  const handleSkipDemo = () => {
    console.log('User skipped demo end screen');
    setCurrentStep(0);
  };
  const overviewKey = `${Number(assessmentStatus.speaking)}${Number(assessmentStatus.listening)}${Number(assessmentStatus.writing)}${Number(assessmentStatus.reading)}`;
  const speakingOutcome = outcome === 'presentation' || outcome === 'interview' || outcome === 'authority' || outcome === 'everyday' ? outcome : 'exam';
  const outcomeLabel = (() => {
    if (!outcome) return '';
    if (outcome === 'custom') {
      if (customText && customText.trim().length > 0) {
        return customText.trim();
      }
      return language === 'de' ? 'Eigenes Ziel' : 'Custom goal';
    }
    type NonCustomOutcome = Exclude<Outcome, 'custom' | null>;
    const labelsDe: Record<NonCustomOutcome, string> = {
      exam: 'Prüfung',
      presentation: 'Präsentation',
      interview: 'Interview',
      authority: 'Behörde',
      everyday: 'Alltag'
    };
    const labelsEn: Record<NonCustomOutcome, string> = {
      exam: 'Exam',
      presentation: 'Presentation',
      interview: 'Interview',
      authority: 'Authority',
      everyday: 'Everyday'
    };
    const labels = language === 'de' ? labelsDe : labelsEn;
    const key = outcome as NonCustomOutcome;
    return labels[key];
  })();
  const customPreview = outcome === 'custom' && customText ? customText.length > 60 ? customText.slice(0, 57) + '…' : customText : null;
  const devSteps: {
    id: number;
    label: string;
  }[] = useMemo(() => {
    if (language === 'de') {
      return [{
        id: 0,
        label: '0 – Ziel wählen'
      }, {
        id: 1,
        label: '1 – Pfad bestätigen'
      }, {
        id: 2,
        label: '2 – Benchmark-Übersicht'
      }, {
        id: 3,
        label: '3 – Sprechen'
      }, {
        id: 4,
        label: '4 – Hören'
      }, {
        id: 5,
        label: '5 – Schreiben'
      }, {
        id: 6,
        label: '6 – Lesen'
      }, {
        id: 7,
        label: '7 – Diagnose'
      }, {
        id: 8,
        label: '8 – Wochenplan'
      }, {
        id: 9,
        label: '9 – Abschluss'
      }, {
        id: 10,
        label: '10 – Demo Ende'
      }];
    }
    return [{
      id: 0,
      label: '0 – Outcome selection'
    }, {
      id: 1,
      label: '1 – Confirm path'
    }, {
      id: 2,
      label: '2 – Benchmark overview'
    }, {
      id: 3,
      label: '3 – Speaking'
    }, {
      id: 4,
      label: '4 – Listening'
    }, {
      id: 5,
      label: '5 – Writing'
    }, {
      id: 6,
      label: '6 – Reading'
    }, {
      id: 7,
      label: '7 – Diagnostics'
    }, {
      id: 8,
      label: '8 – Week plan'
    }, {
      id: 9,
      label: '9 – Success'
    }, {
      id: 10,
      label: '10 – Demo End'
    }];
  }, [language]);
  const devTitle = language === 'de' ? 'Dev-Navigation' : 'Dev navigation';
  const devIntro = language === 'de' ? 'Wähle einen Screen direkt, ohne den Flow durchzugehen. Der Zustand (Ziel, Pfad, Checks) bleibt erhalten.' : 'Jump directly to any screen without running the full flow. State (outcome, path, checks) stays as is.';
  const devResetLabel = language === 'de' ? 'Flow zurücksetzen' : 'Reset flow';
  const devCurrentLabel = language === 'de' ? 'Aktueller Schritt' : 'Current step';
  const renderCurrentStep = () => <>
      {currentStep === 0 && <OutcomeSelection language={language} onContinue={handleOutcomeSelection} data={{
      id: 'outcome-selection'
    }} data-id="element-1" />}

      {currentStep === 1 && <ConfirmPathScreen initialPath={selectedPath} onProceed={handlePathConfirm} language={language} data-id="confirm-path" />}

      {currentStep === 2 && <BenchmarkOverview key={overviewKey} checkProgress={checkProgress} assessmentStatus={assessmentStatus} onStartSpeaking={() => handleStartAssessment('speaking')} onStartListening={() => handleStartAssessment('listening')} onStartWriting={() => handleStartAssessment('writing')} onStartReading={() => handleStartAssessment('reading')} language={language} data-id="benchmark-overview" />}

      {currentStep === 3 && <SpeakingScreen language={language} outcome={speakingOutcome} onContinue={() => handleAssessmentComplete('speaking')} data-id="speaking-screen" />}

      {currentStep === 4 && <ListeningAssessment language={language} onContinue={() => handleAssessmentComplete('listening')} data-id="listening-assessment" />}

      {currentStep === 5 && <OnboardingWriting outcome={outcome || 'exam'} language={language} onContinue={() => handleAssessmentComplete('writing')} data-id="onboarding-writing" />}

      {currentStep === 6 && <OnboardingScreen7 mode="embedded" language={language} onComplete={() => handleAssessmentComplete('reading')} data-id="onboarding-screen-7" />}

      {currentStep === 7 && <DiagnosticResultsScreen diagnostics={diagnosticData} language={language} onAcceptPlan={handleAcceptPlan} onAdjustPlan={handleAcceptPlan} data-id="diagnostic-results" />}

      {currentStep === 8 && <WeekPlanScreen language={language} onAccept={handleWeekPlanAccept} data-id="week-plan" />}

      {currentStep === 9 && <OnboardingSuccess language={language} onStartFirstSession={handleStartFirstSession} onGoToHomeHub={handleGoToHomeHub} onboardingDuration={12} data-id="onboarding-success" />}

      {currentStep === 10 && <DemoEndScreen language={language} onSubmitEmail={handleSubmitEmail} onSkip={handleSkipDemo} data-id="demo-end-screen" />}
    </>;

  // Mobile Layout
  if (isNarrow) {
    return <div className="w-screen min-h-screen bg-gray-100 flex justify-center" data-id="element-2">
        <div style={{
        width: '100%',
        maxWidth: 460,
        padding: '12px 12px 24px'
      }} data-id="element-3">
          {/* Phone-Container */}
          <div className="bg-white shadow-2xl overflow-hidden" style={{
          borderRadius: 24,
          height: '844px',
          display: 'flex',
          flexDirection: 'column'
        }} data-id="element-4">
            <div className="relative" style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden'
          }} data-id="element-5">
              {/* Content-Wrapper */}
              <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              height: '100%'
            }} data-id="element-6">
                {renderCurrentStep()}
              </div>

              {/* Deckblatt-Overlay (mobile) – nutzt MOBILE Cover */}
              <CoverOverlay show={showCover} onDismiss={() => setShowCover(false)} isMobile={true} data-id="element-7" />
            </div>
          </div>

          {/* Controls (Zurück + Ziel + Language) */}
          <div style={{
          marginTop: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontFamily: disceTypography.fontFamily
        }} data-id="element-8">
            {currentStep > 0 && <button onClick={handleGoBack} style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 10px',
            borderRadius: 8,
            backgroundColor: disceColors.neutral[0],
            color: disceColors.primary[900],
            fontSize: 12,
            fontWeight: disceTypography.button.fontWeight,
            lineHeight: disceTypography.button.lineHeight,
            border: `1px solid ${disceColors.neutral[600]}`,
            cursor: 'pointer',
            whiteSpace: 'nowrap'
          }} data-id="element-9">
                <ChevronLeft size={18} data-id="element-10" />
                <span data-id="element-11">{language === 'de' ? 'Zurück' : 'Back'}</span>
              </button>}

            {outcome && outcomeLabel && <div style={{
            padding: '6px 10px',
            borderRadius: 999,
            backgroundColor: 'rgba(255,255,255,0.95)',
            maxWidth: 150,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
            flex: 1,
            minWidth: 0
          }} data-id="element-12">
                <span style={{
              fontSize: 10,
              fontWeight: 500,
              lineHeight: 1.2,
              color: '#555555',
              textTransform: 'uppercase',
              letterSpacing: '0.03em'
            }} data-id="element-13">
                  {language === 'de' ? 'Ziel' : 'Goal'}
                </span>
                <span style={{
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.3,
              color: '#111111',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }} data-id="element-14">
                  {outcome === 'custom' && customPreview ? language === 'de' ? 'Eigenes Ziel' : 'Custom goal' : outcomeLabel}
                </span>
              </div>}

            <button onClick={toggleLanguage} style={{
            padding: '6px 10px',
            borderRadius: 999,
            backgroundColor: '#2563EB',
            color: '#FFFFFF',
            border: 'none',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            whiteSpace: 'nowrap'
          }} data-id="element-15">
              {language === 'de' ? 'DE' : 'EN'}
            </button>
          </div>

          {/* Dev-Navigation */}
          <div className="bg-white shadow-md rounded-xl p-4" style={{
          marginTop: 16,
          fontFamily: 'system-ui, sans-serif'
        }} data-id="element-16">
            <h2 style={{
            fontSize: 16,
            fontWeight: 600,
            marginBottom: 8
          }} data-id="element-17">
              {devTitle}
            </h2>
            <p style={{
            fontSize: 12,
            color: '#555',
            marginBottom: 12,
            lineHeight: 1.4
          }} data-id="element-18">
              {devIntro}
            </p>
            <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            maxHeight: 260,
            overflowY: 'auto',
            paddingRight: 2
          }} data-id="element-19">
              {devSteps.map(step => {
              const isActive = currentStep === step.id;
              return <button key={step.id} onClick={() => setCurrentStep(step.id)} style={{
                flex: '1 1 48%',
                textAlign: 'left',
                fontSize: 11,
                padding: '6px 8px',
                borderRadius: 6,
                border: 'none',
                cursor: 'pointer',
                backgroundColor: isActive ? '#2563EB' : '#F3F4F6',
                color: isActive ? '#FFFFFF' : '#111827',
                fontWeight: isActive ? 600 : 400
              }} data-id="element-20">
                    {step.label}
                  </button>;
            })}
            </div>
            <div style={{
            marginTop: 10,
            borderTop: '1px solid #E5E7EB',
            paddingTop: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 8
          }} data-id="element-21">
              <button onClick={() => {
              resetOnboarding();
              setCurrentStep(0);
            }} style={{
              width: '100%',
              padding: '6px 8px',
              fontSize: 12,
              borderRadius: 6,
              border: '1px solid #D1D5DB',
              backgroundColor: '#FFFFFF',
              cursor: 'pointer'
            }} data-id="element-22">
                {devResetLabel}
              </button>
              <div style={{
              fontSize: 11,
              color: '#6B7280'
            }} data-id="element-23">
                {devCurrentLabel}: <strong data-id="element-24">{currentStep}</strong>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }

  // Desktop Layout
  return <div className="w-screen h-screen bg-gray-100 flex items-center justify-center" data-id="element-25">
      <div className="flex items-start gap-6 px-4 py-4" data-id="element-26">
        {/* Dev-Control-Leiste links */}
        <div className="bg-white shadow-md rounded-xl p-4" style={{
        width: '260px',
        fontFamily: 'system-ui, sans-serif'
      }} data-id="element-27">
          <h2 style={{
          fontSize: '16px',
          fontWeight: 600,
          marginBottom: '8px'
        }} data-id="element-28">
            {devTitle}
          </h2>
          <p style={{
          fontSize: '12px',
          color: '#555',
          marginBottom: '12px',
          lineHeight: 1.4
        }} data-id="element-29">
            {devIntro}
          </p>
          <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          maxHeight: '400px',
          overflowY: 'auto',
          paddingRight: '4px'
        }} data-id="element-30">
            {devSteps.map(step => {
            const isActive = currentStep === step.id;
            return <button key={step.id} onClick={() => setCurrentStep(step.id)} style={{
              width: '100%',
              textAlign: 'left',
              fontSize: '12px',
              padding: '6px 8px',
              borderRadius: '6px',
              border: 'none',
              cursor: 'pointer',
              backgroundColor: isActive ? '#2563EB' : '#F3F4F6',
              color: isActive ? '#FFFFFF' : '#111827',
              fontWeight: isActive ? 600 : 400
            }} data-id="element-31">
                  {step.label}
                </button>;
          })}
          </div>
          <div style={{
          marginTop: '12px',
          borderTop: '1px solid #E5E7EB',
          paddingTop: '10px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }} data-id="element-32">
            <button onClick={() => {
            resetOnboarding();
            setCurrentStep(0);
          }} style={{
            width: '100%',
            padding: '6px 8px',
            fontSize: '12px',
            borderRadius: '6px',
            border: '1px solid #D1D5DB',
            backgroundColor: '#FFFFFF',
            cursor: 'pointer'
          }} data-id="element-33">
              {devResetLabel}
            </button>
            <div style={{
            fontSize: '11px',
            color: '#6B7280'
          }} data-id="element-34">
              {devCurrentLabel}: <strong data-id="element-35">{currentStep}</strong>
            </div>
          </div>
        </div>

        {/* Phone container */}
        <div className="bg-white overflow-hidden shadow-2xl" style={{
        width: '460px',
        height: '844px',
        display: 'flex',
        flexDirection: 'column'
      }} data-id="element-36">
          <div className="relative" style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden'
        }} data-id="element-37">
            {/* Language Toggle + Zielanzeige */}
            <div style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            zIndex: 60,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }} data-id="element-38">
              {outcome && outcomeLabel && <div style={{
              padding: '6px 10px',
              borderRadius: '999px',
              backgroundColor: 'rgba(255,255,255,0.95)',
              maxWidth: '180px',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 1px 4px rgba(0,0,0,0.15)'
            }} data-id="element-39">
                  <span style={{
                fontSize: 10,
                fontWeight: 500,
                lineHeight: 1.2,
                color: '#555555',
                textTransform: 'uppercase',
                letterSpacing: '0.03em'
              }} data-id="element-40">
                    {language === 'de' ? 'Ziel' : 'Goal'}
                  </span>
                  <span style={{
                fontSize: 12,
                fontWeight: 600,
                lineHeight: 1.3,
                color: '#111111'
              }} data-id="element-41">
                    {outcome === 'custom' && customPreview ? language === 'de' ? 'Eigenes Ziel' : 'Custom goal' : outcomeLabel}
                  </span>
                  {outcome === 'custom' && customPreview && <span style={{
                fontSize: 10,
                lineHeight: 1.3,
                color: '#555555'
              }} data-id="element-42">
                      "{customPreview}"
                    </span>}
                </div>}

              <button onClick={toggleLanguage} style={{
              padding: '6px 10px',
              borderRadius: '999px',
              backgroundColor: '#2563EB',
              color: '#FFFFFF',
              border: 'none',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 2px 6px rgba(0,0,0,0.2)'
            }} aria-label={language === 'de' ? 'Switch interface language to English' : 'Sprache auf Deutsch umstellen'} data-id="element-43">
                {language === 'de' ? 'DE' : 'EN'}
              </button>
            </div>

            {/* Back Button */}
            {currentStep > 0 && <div style={{
            position: 'absolute',
            top: '16px',
            left: '16px',
            zIndex: 50
          }} data-id="element-44">
                <button onClick={handleGoBack} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 12px',
              borderRadius: '8px',
              backgroundColor: disceColors.neutral[0],
              color: disceColors.primary[900],
              fontSize: disceTypography.button.fontSize,
              fontWeight: disceTypography.button.fontWeight,
              lineHeight: disceTypography.button.lineHeight,
              fontFamily: disceTypography.fontFamily,
              border: `1px solid ${disceColors.neutral[600]}`,
              cursor: 'pointer',
              transition: 'all 0.2s',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }} onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#f9f9f9';
              e.currentTarget.style.transform = 'translateX(-2px)';
            }} onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = disceColors.neutral[0];
              e.currentTarget.style.transform = 'translateX(0)';
            }} data-id="element-45">
                  <ChevronLeft size={20} data-id="element-46" />
                  <span data-id="element-47">{language === 'de' ? 'Zurück' : 'Back'}</span>
                </button>
              </div>}

            {/* Content-Wrapper */}
            <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }} data-id="element-48">
              {renderCurrentStep()}
            </div>

            {/* Deckblatt-Overlay (Desktop) – nutzt DESKTOP Cover */}
            <CoverOverlay show={showCover} onDismiss={() => setShowCover(false)} isMobile={false} data-id="element-49" />
          </div>
        </div>
      </div>
    </div>;
}